import { BYTES_IN_KB, BYTES_IN_MB } from "@constants";

// Enhanced file validation utility for secure file uploads
interface FileValidationConfig {
  maxFileSize: number;
  minFileSize: number;
  maxFileNameLength: number;
  allowedExtensions: readonly string[];
  allowedMimeTypes: readonly string[];
}

export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
}

// Security constants
const SUSPICIOUS_FILE_SIGNATURES = {
  // Archive formats
  ZIP: [0x50, 0x4b, 0x03, 0x04], // ZIP file signature
  RAR: [0x52, 0x61, 0x72, 0x21], // RAR file signature
  SEVEN_ZIP: [0x37, 0x7a, 0xbc, 0xaf], // 7z file signature
  TAR: [0x75, 0x73, 0x74, 0x61], // TAR file signature
  GZIP: [0x1f, 0x8b], // GZIP file signature

  // Executable formats
  PE_EXECUTABLE: [0x4d, 0x5a], // Windows PE executable
  ELF_EXECUTABLE: [0x7f, 0x45, 0x4c, 0x46], // Linux ELF executable
  MACH_O: [0xfe, 0xed, 0xfa, 0xce], // macOS Mach-O executable

  // Encrypted/Password protected
  ENCRYPTED_ZIP: [0x50, 0x4b, 0x03, 0x04], // ZIP with encryption flag
  ENCRYPTED_PDF: [0x25, 0x50, 0x44, 0x46], // PDF (needs content analysis)

  // Script files
  BATCH_FILE: [0x40, 0x65, 0x63, 0x68], // @echo (batch file)
  POWERSHELL: [0x23, 0x20, 0x50, 0x6f], // # Po (PowerShell comment)
};

const DANGEROUS_EXTENSIONS = [
  // Executable files
  ".exe",
  ".com",
  ".bat",
  ".cmd",
  ".scr",
  ".pif",
  ".vbs",
  ".vbe",
  ".js",
  ".jse",
  ".wsf",
  ".wsh",
  ".msc",
  ".msi",
  ".msp",
  ".dll",
  ".application",
  ".gadget",
  ".msp",
  ".jar",
  ".app",
  ".deb",
  ".rpm",
  ".dmg",
  ".pkg",

  // Archive files
  ".zip",
  ".rar",
  ".7z",
  ".tar",
  ".gz",
  ".bz2",
  ".xz",
  ".cab",
  ".iso",
  ".ace",
  ".arj",
  ".lzh",
  ".sit",
  ".sitx",
  ".sea",
  ".bin",
  ".hqx",

  // Script files
  ".sh",
  ".bash",
  ".zsh",
  ".fish",
  ".csh",
  ".tcsh",
  ".ps1",
  ".psm1",
  ".psd1",
  ".ps1xml",
  ".pssc",
  ".psrc",
  ".cdxml",

  // Document macros
  ".docm",
  ".dotm",
  ".xlsm",
  ".xltm",
  ".xlam",
  ".pptm",
  ".potm",
  ".ppam",
  ".ppsm",
  ".sldm",

  // Other dangerous
  ".lnk",
  ".url",
  ".desktop",
  ".reg",
  ".inf",
  ".sys",
  ".drv",
];

const ARCHIVE_MIME_TYPES = [
  "application/zip",
  "application/x-zip-compressed",
  "application/x-rar-compressed",
  "application/x-7z-compressed",
  "application/x-tar",
  "application/gzip",
  "application/x-gzip",
  "application/x-bzip2",
  "application/x-compress",
  "application/x-compressed",
  "application/octet-stream", // Generic binary - needs further inspection
];

// Helper functions
const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf(".");
  return lastDotIndex === -1
    ? ""
    : fileName.substring(lastDotIndex).toLowerCase();
};

const hasMultipleExtensions = (
  fileName: string,
  allowedExtensions: readonly string[],
): boolean => {
  const parts = fileName.split(".");
  if (parts.length <= 2) return false;

  // Check if there are multiple valid extensions
  const potentialExtensions = parts.slice(1);
  let validExtensionCount = 0;

  for (const ext of potentialExtensions) {
    const fullExt = "." + ext.toLowerCase();
    if (
      DANGEROUS_EXTENSIONS.includes(fullExt) ||
      allowedExtensions.some((allowed) => allowed.toLowerCase() === fullExt)
    ) {
      validExtensionCount++;
    }
  }

  return validExtensionCount > 1;
};

const isAllowedExtension = (
  extension: string,
  allowedExtensions: readonly string[],
): boolean => {
  return allowedExtensions.some(
    (allowed) => allowed.toLowerCase() === extension.toLowerCase(),
  );
};

const isAllowedMimeType = (
  mimeType: string,
  allowedMimeTypes: readonly string[],
): boolean => {
  return allowedMimeTypes.some(
    (allowed) => allowed.toLowerCase() === mimeType.toLowerCase(),
  );
};

const isDangerousExtension = (extension: string): boolean => {
  return DANGEROUS_EXTENSIONS.includes(extension.toLowerCase());
};

const readFileHeader = async (
  file: File,
  size: number,
): Promise<ArrayBuffer | null> => {
  try {
    const slice = file.slice(0, size);
    return await slice.arrayBuffer();
  } catch (error) {
    console.error("Error reading file header:", error);
    return null;
  }
};

const matchesSignature = (bytes: Uint8Array, signature: number[]): boolean => {
  if (bytes.length < signature.length) return false;

  for (let i = 0; i < signature.length; i++) {
    if (bytes[i] !== signature[i]) return false;
  }

  return true;
};

const isArchiveFile = async (file: File): Promise<boolean> => {
  // Check MIME type first
  if (ARCHIVE_MIME_TYPES.includes(file.type.toLowerCase())) {
    return true;
  }

  // Check file signature
  const buffer = await readFileHeader(file, 8);
  if (!buffer) return false;

  const bytes = new Uint8Array(buffer);

  // Check for archive signatures
  for (const [format, signature] of Object.entries(
    SUSPICIOUS_FILE_SIGNATURES,
  )) {
    if (
      format.includes("ZIP") ||
      format.includes("RAR") ||
      format.includes("SEVEN_ZIP") ||
      format.includes("TAR") ||
      format.includes("GZIP")
    ) {
      if (matchesSignature(bytes, signature)) {
        return true;
      }
    }
  }

  return false;
};

const getExpectedSignature = (mimeType: string): number[] | null => {
  const signatureMap: { [key: string]: number[] } = {
    "image/jpeg": [0xff, 0xd8, 0xff],
    "image/png": [0x89, 0x50, 0x4e, 0x47],
    "image/gif": [0x47, 0x49, 0x46, 0x38],
    "application/pdf": [0x25, 0x50, 0x44, 0x46],
    "application/json": [0x7b], // { character
    "video/mp4": [0x66, 0x74, 0x79, 0x70], // ftyp (at offset 4)
    "video/quicktime": [0x66, 0x74, 0x79, 0x70], // ftyp (at offset 4)
  };

  return signatureMap[mimeType.toLowerCase()] || null;
};

const validateFileSignature = async (
  file: File,
): Promise<FileValidationResult> => {
  const errors: string[] = [];
  const buffer = await readFileHeader(file, 16);

  if (!buffer) {
    errors.push("Unable to read file header for validation");
    return { isValid: false, errors };
  }

  const bytes = new Uint8Array(buffer);

  // Check for executable signatures
  for (const [format, signature] of Object.entries(
    SUSPICIOUS_FILE_SIGNATURES,
  )) {
    if (format.includes("EXECUTABLE") || format.includes("MACH_O")) {
      if (matchesSignature(bytes, signature)) {
        errors.push(`File contains executable code signature (${format})`);
      }
    }
  }

  // Validate that file signature matches declared MIME type
  const expectedSignature = getExpectedSignature(file.type);
  if (expectedSignature && !matchesSignature(bytes, expectedSignature)) {
    // For MP4/MOV files, check at offset 4 (after the box size)
    if (file.type === "video/mp4" || file.type === "video/quicktime") {
      if (
        bytes.length >= 8 &&
        !matchesSignature(bytes.slice(4), expectedSignature)
      ) {
        errors.push("File signature does not match declared file type");
      }
    } else {
      errors.push("File signature does not match declared file type");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const isPasswordProtected = async (file: File): Promise<boolean> => {
  const buffer = await readFileHeader(file, 1024);
  if (!buffer) return false;

  const bytes = new Uint8Array(buffer);

  // Check for ZIP encryption flag
  if (matchesSignature(bytes, SUSPICIOUS_FILE_SIGNATURES.ZIP)) {
    // Check bit 0 of general purpose bit flag (offset 6-7 in ZIP local header)
    if (bytes.length > 7 && (bytes[6] & 0x01) === 0x01) {
      return true;
    }
  }

  // Check for PDF encryption
  if (file.type === "application/pdf") {
    const content = new TextDecoder().decode(bytes);
    if (content.includes("/Encrypt") || content.includes("/Filter")) {
      return true;
    }
  }

  // Check for Office document encryption
  if (
    file.type.includes("officedocument") ||
    file.type.includes("ms-excel") ||
    file.type.includes("ms-powerpoint") ||
    file.type.includes("msword")
  ) {
    // Office documents are ZIP-based, check for encryption
    const content = new TextDecoder().decode(bytes);
    if (
      content.includes("EncryptionInfo") ||
      content.includes("EncryptedPackage")
    ) {
      return true;
    }
  }

  return false;
};

const calculateEntropy = (bytes: Uint8Array): number => {
  const frequency = new Array(256).fill(0);

  for (let i = 0; i < bytes.length; i++) {
    frequency[bytes[i]]++;
  }

  let entropy = 0;
  const length = bytes.length;

  for (const count of frequency) {
    if (count > 0) {
      const probability = count / length;
      entropy -= probability * Math.log2(probability);
    }
  }

  return entropy;
};

const hasTextContent = (bytes: Uint8Array): boolean => {
  let printableCount = 0;
  const sampleSize = Math.min(bytes.length, 512);

  for (let i = 0; i < sampleSize; i++) {
    const byte = bytes[i];
    if (
      (byte >= 32 && byte <= 126) ||
      byte === 9 ||
      byte === 10 ||
      byte === 13
    ) {
      printableCount++;
    }
  }

  return printableCount / sampleSize > 0.7;
};

const hasKnownFileHeader = (bytes: Uint8Array): boolean => {
  const knownHeaders = [
    [0xff, 0xd8, 0xff], // JPEG
    [0x89, 0x50, 0x4e, 0x47], // PNG
    [0x47, 0x49, 0x46, 0x38], // GIF
    [0x25, 0x50, 0x44, 0x46], // PDF
    [0x50, 0x4b, 0x03, 0x04], // ZIP
    [0x7b, 0x22], // JSON starting with {"
    [0x3c, 0x3f, 0x78, 0x6d], // XML
  ];

  // Check for MP4/MOV files (ftyp box at offset 4)
  if (bytes.length >= 8) {
    const ftypSignature = [0x66, 0x74, 0x79, 0x70]; // "ftyp"
    if (matchesSignature(bytes.slice(4), ftypSignature)) {
      return true;
    }
  }

  for (const header of knownHeaders) {
    if (matchesSignature(bytes, header)) {
      return true;
    }
  }

  return false;
};

const isEncrypted = async (file: File): Promise<boolean> => {
  const buffer = await readFileHeader(file, 1024);
  if (!buffer) return false;

  const bytes = new Uint8Array(buffer);

  // High entropy check (simple heuristic for encryption)
  const entropy = calculateEntropy(bytes);
  const entropyThreshold = 7.5; // Encrypted files typically have high entropy

  if (entropy > entropyThreshold && file.size > 1024) {
    // Additional checks to avoid false positives
    const hasText = hasTextContent(bytes);
    const hasKnownHeader = hasKnownFileHeader(bytes);

    return !hasText && !hasKnownHeader;
  }

  return false;
};

const formatFileSize = (bytes: number): string => {
  const sizes = ["Bytes", "KB", "MB", "GB"];
  if (bytes === 0) return "0 Bytes";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
};

export const getFileSizeValidationError = (
  fileSize: number,
  maxFileSize: number,
) => {
  return `File size (${formatFileSize(fileSize)}) exceeds maximum allowed size (${formatFileSize(maxFileSize)})`;
};

export const getFileExtensionValidationError = (fileExtension: string) => {
  return `File extension '${fileExtension}' is not allowed`;
};

const validateFile = async (file: File, config: FileValidationConfig) => {
  const errors: string[] = [];

  // 1. File size validation
  if (file.size > config.maxFileSize) {
    errors.push(
      getFileSizeValidationError(file.size, config.maxFileSize),
      `File size (${formatFileSize(file.size)}) exceeds maximum allowed size (${formatFileSize(config.maxFileSize)})`,
    );
  }

  if (file.size < config.minFileSize) {
    errors.push(
      `File size (${formatFileSize(file.size)}) is below minimum required size (${formatFileSize(config.minFileSize)})`,
    );
  }

  if (file.size === 0) {
    errors.push("Empty files are not allowed");
  }

  // 2. File name validation
  if (file.name.length > config.maxFileNameLength) {
    errors.push(
      `File name is too long (${file.name.length} characters). Maximum allowed: ${config.maxFileNameLength}`,
    );
  }

  if (file.name.length === 0) {
    errors.push("File name cannot be empty");
  }

  // 3. Multiple extension validation
  if (hasMultipleExtensions(file.name, config.allowedExtensions)) {
    errors.push(
      "Files with multiple extensions are not allowed (e.g., .exe.png)",
    );
  }

  // 4. File extension validation (case-insensitive)
  const fileExtension = getFileExtension(file.name);
  if (!isAllowedExtension(fileExtension, config.allowedExtensions)) {
    errors.push(getFileExtensionValidationError(fileExtension));
  }

  // 5. MIME type validation
  if (!isAllowedMimeType(file.type, config.allowedMimeTypes)) {
    errors.push(`File type '${file.type}' is not allowed`);
  }

  // 6. Dangerous extension check
  if (isDangerousExtension(fileExtension)) {
    errors.push(
      `File extension '${fileExtension}' is blocked for security reasons`,
    );
  }

  // 7. Archive format detection
  if (await isArchiveFile(file)) {
    errors.push("Archive files are not allowed");
  }

  // 8. File signature validation
  const signatureValidation = await validateFileSignature(file);
  if (!signatureValidation.isValid) {
    errors.push(...signatureValidation.errors);
  }

  // 9. Password protection check
  if (await isPasswordProtected(file)) {
    errors.push("Password protected files are not allowed");
  }

  // 10. Encryption detection
  if (await isEncrypted(file)) {
    errors.push("Encrypted files are not allowed");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Configuration presets for different file types
const FILE_VALIDATION_CONFIGS = {
  SCORECARD: {
    maxFileSize: 10 * BYTES_IN_MB, // 10MB
    minFileSize: 1, // 1 byte
    maxFileNameLength: 255,
    allowedExtensions: [".json"],
    allowedMimeTypes: ["application/json", "text/json"],
  },
  VIDEO: {
    maxFileSize: 500 * BYTES_IN_MB, // 500MB
    minFileSize: BYTES_IN_KB, // 1KB
    maxFileNameLength: 255,
    allowedExtensions: [".mp4", ".mov"],
    allowedMimeTypes: ["video/mp4", "video/quicktime"],
  },
  IMAGE: {
    maxFileSize: 15 * BYTES_IN_MB, // 15MB
    minFileSize: BYTES_IN_KB, // 1KB
    maxFileNameLength: 255,
    allowedExtensions: [".png", ".jpg", ".jpeg"],
    allowedMimeTypes: ["image/png", "image/jpeg", "image/jpg"],
  },
  COMMENT_ATTACHMENT: {
    maxFileSize: 5 * BYTES_IN_MB, // 5MB
    minFileSize: 1, // 1 byte
    maxFileNameLength: 255,
    allowedExtensions: [".png", ".jpg", ".jpeg"],
    allowedMimeTypes: ["image/png", "image/jpeg", "image/jpg"],
  },
} as const;

export const validateScorecard = (file: File) =>
  validateFile(file, FILE_VALIDATION_CONFIGS.SCORECARD);
export const validateVideo = (file: File) =>
  validateFile(file, FILE_VALIDATION_CONFIGS.VIDEO);
export const validateImage = (file: File) =>
  validateFile(file, FILE_VALIDATION_CONFIGS.IMAGE);
export const validateCommentAttachment = (file: File) =>
  validateFile(file, FILE_VALIDATION_CONFIGS.COMMENT_ATTACHMENT);
